import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeyValue = (formData.get('mobile_alt') as string || '').trim();

    if (honeyValue) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'mobile_alt' && value) {
          payload.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9rgvgnnahb7382ovmcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore JSON parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络连接异常，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: siteInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://your-site.com/',
    description: siteInfo.description,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.email,
      telephone: siteInfo.phone,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: siteInfo.address,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <SEOMeta
        title="关于我们"
        description={`${siteInfo.name} - ${siteInfo.description}`}
        keywords="里番动漫,动漫推荐,关于我们,动漫文化"
        canonicalUrl="https://your-site.com/about"
        ogType="website"
      />
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* 品牌介绍 */}
        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <span className="text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1 rounded-full whitespace-nowrap">
                  关于我们
                </span>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mt-4 mb-6 leading-tight">
                  {siteInfo.name}
                </h1>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {siteInfo.name}创立于{siteInfo.founded}年，是国内领先的里番动漫内容推荐与资讯平台。
                  我们致力于为成人动漫爱好者提供专业、全面的里番动漫推荐服务，
                  帮助用户快速发现优质内容，深入了解里番动漫文化。
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  自成立以来，我们始终坚持以用户需求为导向，不断优化内容质量和推荐算法。
                  我们的编辑团队由一群热爱动漫文化的资深爱好者组成，
                  他们用专业的眼光和热情的态度，为每一位用户筛选和推荐最值得观看的作品。
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  我们相信，优质的里番动漫不仅是娱乐，更是一种值得深入探索的文化现象。
                  通过我们的平台，希望能够帮助更多人理解和欣赏这一独特的艺术形式。
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20and%20artistic%20anime%20style%20illustration%20of%20a%20cozy%20creative%20studio%20workspace%20with%20screens%20displaying%20anime%20art%2C%20bookshelves%20filled%20with%20manga%2C%20warm%20ambient%20lighting%2C%20creative%20team%20atmosphere%2C%20rich%20crimson%20and%20golden%20accents%2C%20professional%20yet%20inviting%20feel&width=800&height=600&seq=about-hero-01&orientation=landscape"
                    alt={`关于${siteInfo.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-16">
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-1">
                  {siteInfo.stats.totalArticles.toLocaleString()}+
                </div>
                <div className="text-xs text-foreground-600">精品文章</div>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-accent-500 mb-1">
                  {siteInfo.stats.totalCategories}
                </div>
                <div className="text-xs text-foreground-600">内容分类</div>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-1">
                  {siteInfo.stats.totalUsers.toLocaleString()}+
                </div>
                <div className="text-xs text-foreground-600">活跃用户</div>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-accent-500 mb-1">
                  {siteInfo.stats.totalReviews.toLocaleString()}+
                </div>
                <div className="text-xs text-foreground-600">用户评价</div>
              </div>
            </div>
          </div>
        </section>

        {/* 口碑评价 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600 mt-2">
                来自真实用户的评价与反馈
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {siteInfo.testimonials.map((t, index) => (
                <div
                  key={index}
                  className="bg-background-50 p-6 rounded-lg border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{t.comment}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 常见问题 (FAQ) */}
        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600 mt-2">
                关于里番动漫和本站的常见疑问解答
              </p>
            </div>
            <div className="space-y-4">
              {siteInfo.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 text-sm cursor-pointer select-none">
                    <span>{faq.question}</span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3 transition-transform duration-200 group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-700 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* 联系我们 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 mt-2">
                如果您有任何问题或建议，欢迎随时联系我们
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-700"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-700"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="bg-background-50 p-6 rounded-lg border border-background-200/70 space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入咨询主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1 text-right">最多500字</p>
                  </div>

                  <input
                    type="text"
                    name="mobile_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-honeypot"
                  />

                  <div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </span>
                      ) : '发送留言'}
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                      <i className="ri-check-line mr-1"></i>
                      感谢您的留言！我们会尽快回复您。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-primary-100 text-primary-800 text-sm text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}